import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { evaluationScoresApi, SaveScoresDto, BulkSaveScoresDto } from "../../../api/evaluationScores";
import { notifications } from "@mantine/notifications";

export const useBidSubmissionScores = (tenderId: string, bidSubmissionId: string) => {
  return useQuery({
    queryKey: ['evaluationScores', tenderId, bidSubmissionId],
    queryFn: async () => {
      const res = await evaluationScoresApi.getBidSubmissionScores(tenderId, bidSubmissionId);
      return res;
    },
    enabled: !!tenderId && !!bidSubmissionId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useSaveBidSubmissionScores = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenderId, bidSubmissionId, data }: { tenderId: string; bidSubmissionId: string; data: SaveScoresDto }) =>
      evaluationScoresApi.saveBidSubmissionScores(tenderId, bidSubmissionId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['evaluationScores', variables.tenderId, variables.bidSubmissionId] });
      notifications.show({
        title: "Success",
        message: "Scores saved successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to save scores",
        color: "red",
      });
    },
  });
};

export const useBulkSaveEvaluationScores = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenderId, data }: { tenderId: string; data: BulkSaveScoresDto }) =>
      evaluationScoresApi.bulkSaveEvaluationScores(tenderId, data),
    onSuccess: (_data, variables) => {
      // Invalidate all score queries for this tender
      queryClient.invalidateQueries({ queryKey: ['evaluationScores', variables.tenderId] });
      notifications.show({
        title: "Success",
        message: "All scores saved successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to save scores",
        color: "red",
      });
    },
  });
};

export const useUpdateEvaluationNotes = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenderId, bidSubmissionId, evaluationNotes }: { tenderId: string; bidSubmissionId: string; evaluationNotes: string }) =>
      evaluationScoresApi.updateEvaluationNotes(tenderId, bidSubmissionId, evaluationNotes),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['bidSubmissions', variables.tenderId] });
      notifications.show({
        title: "Success",
        message: "Evaluation notes updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to update evaluation notes",
        color: "red",
      });
    },
  });
};

